import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import { Empty, Spin } from "antd";

/**
 * IATA 油价周度趋势折线图
 * - 每条已生效的周记录是一个点（横轴 = week_end_date，按时间从旧到新）
 * - 纵轴：Global $/MT
 * - 点击某个点 → 回调该点所在月份（YYYY-MM），供页面跳回周数据表筛选
 */
const FuelWeeklyTrendChart = ({ records = [], loading = false, footer = null, onSelectMonth, height = 320 }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    // 回调放进 ref：父组件每次渲染传入新函数时不重建图表，避免缩放位置被重置
    const onSelectMonthRef = useRef(onSelectMonth);
    onSelectMonthRef.current = onSelectMonth;

    const points = useMemo(
        () =>
            records
                .filter((item) => item?.week_end_date && item.global_price_per_mt != null && Number.isFinite(Number(item.global_price_per_mt)))
                .map((item) => ({ date: String(item.week_end_date).slice(0, 10), value: Number(item.global_price_per_mt) }))
                .sort((left, right) => left.date.localeCompare(right.date)),
        [records],
    );

    useEffect(() => {
        if (!chartRef.current || !points.length) {
            chartInstance.current?.dispose();
            chartInstance.current = null;
            return undefined;
        }
        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }
        const chart = chartInstance.current;
        chart.setOption(
            {
                grid: { left: 56, right: 24, top: 24, bottom: points.length > 26 ? 64 : 40 },
                tooltip: {
                    trigger: "axis",
                    formatter: (params) => {
                        const point = Array.isArray(params) ? params[0] : params;
                        return `周结束日期：${point.axisValue}<br/>Global：${Number(point.value).toFixed(2)} $/MT`;
                    },
                },
                xAxis: {
                    type: "category",
                    boundaryGap: false,
                    data: points.map((point) => point.date),
                    axisLabel: { hideOverlap: true },
                },
                yAxis: { type: "value", scale: true, name: "$/MT" },
                dataZoom: points.length > 26 ? [{ type: "inside" }, { type: "slider", height: 18, bottom: 12 }] : [],
                series: [
                    {
                        name: "Global $/MT",
                        type: "line",
                        data: points.map((point) => point.value),
                        showSymbol: points.length <= 60,
                        symbolSize: 6,
                        lineStyle: { width: 2, color: "#1677ff" },
                        itemStyle: { color: "#1677ff" },
                    },
                ],
            },
            true,
        );
        chart.off("click");
        chart.on("click", (event) => {
            const date = points[event.dataIndex]?.date;
            if (date) {
                onSelectMonthRef.current?.(date.slice(0, 7));
            }
        });
        const handleResize = () => chart.resize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [points]);

    useEffect(
        () => () => {
            chartInstance.current?.dispose();
            chartInstance.current = null;
        },
        [],
    );

    if (!points.length) {
        return <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, background: "#fff", padding: 24 }}>{loading ? <Spin style={{ display: "block", margin: "48px auto" }} /> : <Empty description="暂无已生效的周油价数据" />}</div>;
    }

    return (
        <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, background: "#fff", padding: 24 }}>
            <Spin spinning={loading}>
                <div ref={chartRef} style={{ width: "100%", height }} />
            </Spin>
            {footer ? <div style={{ textAlign: "center", color: "rgba(0,0,0,0.45)", fontSize: 12, marginTop: 8 }}>{footer}</div> : null}
        </div>
    );
};

export default FuelWeeklyTrendChart;
